import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadge } from '@angular/material/badge';
import { MatLine } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationStart, Router } from '@angular/router';
import { filter, map } from 'rxjs';

import { PermissionsService } from '@core/services/permissions.service';
import { ACCOUNT_STORE } from '@core/stores/account.store';
import { APP_STORE } from '@core/stores/app.store';
import { MenuListLoaderComponent } from '@shared/components/loaders/menu-list-loader/menu-list-loader.component';
import { PermissionsType } from '@shared/models/permissions-type';
import { SidenavMenuItem } from '@shared/models/sidenav-menu-item';
import { ZendeskService } from '@zendesk/services/zendesk.service';
import { ZENDESK_STORE } from '@zendesk/stores/zendesk.store';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,

    MatBadge,
    MatIcon,
    MatLine,
    MatListItem,
    MatNavList,
    MatTooltip,

    MenuListLoaderComponent
  ]
})
export class SidenavComponent {
  readonly #router = inject(Router);
  readonly #appStore = inject(APP_STORE);
  readonly #accountStore = inject(ACCOUNT_STORE);
  readonly #permissionsService = inject(PermissionsService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #zendeskService = inject(ZendeskService);
  readonly #zendeskStore = inject(ZENDESK_STORE);

  readonly projectSetupLoaded = this.#appStore.projectSetup.loaded;
  readonly sidenavPinned = this.#appStore.sidenavPinned;
  readonly permissions = this.#accountStore.permissions.entities;
  readonly loading = computed(
    () =>
      this.#appStore.clientSetup.loading() ||
      this.#appStore.projectSetup.loading()
  );
  readonly menuItems = computed(() => this.buildMenuItems());

  readonly activeRoute = signal<string>('');

  readonly canShowSupport = this.#zendeskStore.canShowWidget;
  readonly supportUnreadCount = this.#zendeskStore.unreadCount;
  readonly isSupportOpen = this.#zendeskStore.isOpen;

  private readonly supportToggle = viewChild('supportToggle', {
    read: ElementRef<HTMLElement>
  });

  constructor() {
    this.#zendeskService.initialize();

    effect(() => {
      this.#zendeskService.registerToggleElement(
        this.supportToggle()?.nativeElement ?? null
      );
    });

    this.#router.events
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart
        ),
        map((event: NavigationStart) => event.url)
      )
      .subscribe(url => {
        this.activeRoute.set(url.split('/')[3]);
      });
  }

  toggleSidenavPinned(): void {
    this.#appStore.toggleSidenavPinned();
  }

  toggleSupport(event: MouseEvent): void {
    event.stopPropagation();
    this.#zendeskService.toggle();
  }

  navigateToItem(link: string): void {
    void this.#router.navigate([
      this.#appStore.selectedClientId(),
      this.#appStore.selectedProjectId(),
      link
    ]);
  }

  //#region Private methods

  private buildMenuItems(): SidenavMenuItem[] {
    return [
      {
        id: 'inspections',
        label: 'Inspections',
        icon: 'view-day',
        link: 'inspections',
        hasAccess: this.#permissionsService.hasPermissions(
          [
            PermissionsType.InspectionView,
            PermissionsType.InspectionViewAssigned
          ],
          undefined,
          false
        )
      },
      {
        id: 'lop',
        label: 'LOP List',
        icon: 'report',
        link: 'lop',
        hasAccess: this.#permissionsService.hasPermissions(
          [PermissionsType.LopView, PermissionsType.LopViewAssigned],
          undefined,
          false
        )
      },
      {
        id: 'all-items',
        label: 'All Items',
        icon: 'format-list-checkbox',
        link: 'all-items',
        hasAccess: this.#permissionsService.hasPermissions(
          [
            PermissionsType.InspectionView,
            PermissionsType.InspectionViewAssigned,
            PermissionsType.LopView,
            PermissionsType.LopViewAssigned
          ],
          undefined,
          false
        )
      },
      {
        id: 'images',
        label: 'Images',
        icon: 'camera',
        link: 'images',
        hasAccess: this.#permissionsService.hasPermissions(
          [
            PermissionsType.InspectionView,
            PermissionsType.InspectionViewAssigned,
            PermissionsType.LopView,
            PermissionsType.LopViewAssigned
          ],
          undefined,
          false
        )
      },
      {
        id: 'templates',
        label: 'Templates',
        icon: 'layers-triple',
        link: 'templates',
        hasAccess: this.#permissionsService.hasPermissions(
          [PermissionsType.InspectionTemplateView],
          undefined,
          false
        )
      },
      {
        id: 'floorplans',
        label: 'Drawings',
        icon: 'vector-square',
        link: 'floorplans',
        hasAccess: this.#permissionsService.hasPermissions(
          [PermissionsType.FloorplanView],
          undefined,
          false
        )
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: 'cog',
        link: 'settings',
        hasAccess: this.#permissionsService.hasPermissions(
          [
            PermissionsType.AttributeView,
            PermissionsType.AccountView,
            PermissionsType.RoleView,
            PermissionsType.InspectionItemStatusView,
            PermissionsType.InspectionReportCustomSectionView,
            PermissionsType.EmailNotificationView
          ],
          undefined,
          false
        )
      }
    ];
  }

  //#endregion
}
